/**
 * DocumentDrawer — slide-in detail for a single hit / citation.
 *
 * Shows the document metadata and the matched chunk text with the search
 * terms highlighted (rendered safely via {@link HighlightedSnippet}). The
 * opened document + chunk are reflected in the URL by the page, so the drawer
 * is deep-linkable (`/search?doc=<id>&chunk=<id>`).
 *
 * Implemented with MUI `Drawer` carrying `role="dialog"` + an accessible name
 * so it is announced and keyboard dismissible.
 */
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { FullDocumentDialog } from "./FullDocumentDialog";
import { HighlightedSnippet } from "./HighlightedSnippet";
import type { ReactElement } from "react";

export interface DocumentDetail {
  documentId: string;
  chunkId: string;
  documentTitle: string | null;
  jurisdictionName: string | null;
  bodyName: string | null;
  meetingDate: string | null;
  documentKind: string | null;
  /** Snippet possibly containing `<mark>` highlight markers. */
  snippetWithHighlights: string;
  /** A verified source URL, when available. */
  sourceUrl: string | null;
}

export interface DocumentDrawerProps {
  open: boolean;
  detail: DocumentDetail | null;
  onClose: () => void;
}

function MetaRow({ label, value }: { label: string; value: string | null }): ReactElement | null {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="span">
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

export function DocumentDrawer({ open, detail, onClose }: DocumentDrawerProps): ReactElement {
  const meetingDate = detail?.meetingDate ? new Date(detail.meetingDate) : null;
  const meetingDateLabel =
    meetingDate && !Number.isNaN(meetingDate.getTime()) ? meetingDate.toLocaleDateString() : null;
  const [fullOpen, setFullOpen] = useState(false);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-label": "Document detail",
          "aria-modal": true,
          sx: { width: { xs: "100%", sm: 420 }, p: 2 },
        },
      }}
    >
      {detail ? (
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Typography variant="h6" component="h2">
              {detail.documentTitle ?? "Untitled document"}
            </Typography>
            <IconButton aria-label="Close document detail" onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Stack spacing={1.5}>
            <MetaRow label="Jurisdiction" value={detail.jurisdictionName} />
            <MetaRow label="Body" value={detail.bodyName} />
            <MetaRow label="Meeting date" value={meetingDateLabel} />
            <MetaRow label="Document kind" value={detail.documentKind} />
            {detail.sourceUrl ? (
              <Box>
                <Typography variant="caption" color="text.secondary" component="span">
                  Source
                </Typography>
                <Typography>
                  <Link href={detail.sourceUrl} target="_blank" rel="noreferrer noopener">
                    Open source document
                  </Link>
                </Typography>
              </Box>
            ) : null}
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2" component="h3" gutterBottom>
              Matched passage
            </Typography>
            <HighlightedSnippet snippet={detail.snippetWithHighlights} component="p" />
          </Box>

          <Button
            variant="outlined"
            startIcon={<OpenInFullIcon />}
            onClick={() => setFullOpen(true)}
            aria-label="View full indexed content"
          >
            View full indexed content
          </Button>
        </Stack>
      ) : null}
      <FullDocumentDialog
        open={fullOpen}
        documentId={detail?.documentId ?? null}
        documentTitle={detail?.documentTitle ?? null}
        onClose={() => setFullOpen(false)}
      />
    </Drawer>
  );
}
